import { Button } from "@/lib/components/Button/Button";
import { Checkbox } from "@/lib/components/Checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { IconFilter, IconUpload, IconX } from "@tabler/icons-react";
import { Badge } from "@/lib/components/Badge";
import { useMemo, useRef, useState, type ReactNode } from "react";
import Image from "@/lib/components/Image";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import { useImagesGridVirtualizer } from "./asset-selector-hooks";

const AssetSelectorDialog = Dialog;

function AssetSelectorLayout({ children }: { children: React.ReactNode }) {
  return (
    <DialogContent
      className="h-full max-h-175 w-full max-w-273 border-none"
      classNameInner="flex min-h-0 flex-col gap-0 overflow-y-hidden p-0 sm:px-0"
      showCloseButton={false}
    >
      {children}
    </DialogContent>
  );
}

function AssetSelectorHeader({
  title,
  isElementsEmpty,
  className,
}: {
  title: string;
  isElementsEmpty: boolean;
  className?: string;
}) {
  return (
    <DialogHeader
      showCloseButton
      variant="bordered"
      className={cn(
        "min-h-18 px-8 pr-5",
        isElementsEmpty && "border-none",
        className,
      )}
    >
      <DialogTitle>{title}</DialogTitle>
    </DialogHeader>
  );
}

function AssetSelectorBody({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-0 flex-1">{children}</div>;
}

function AssetSelectorSidebar({
  children,
  isElementsEmpty,
  className,
}: {
  children: React.ReactNode;
  isElementsEmpty: boolean;
  className?: string;
}) {
  return (
    <aside
      className={cn(
        "flex w-71.25 flex-col border-grey-200",
        isElementsEmpty ? "-mr-px border-t" : "border-r",
        className,
      )}
    >
      {children}
    </aside>
  );
}

function AssetSelectorContent({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative flex min-h-0 flex-1 flex-col">{children}</div>
  );
}

function AssetSelectorBottomBar({
  selectedCount,
  selectedLabel,
  onClear,
  children,
  className,
}: {
  selectedCount: number;
  selectedLabel?: string;
  onClear: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  if (selectedCount === 0) return null;

  return (
    <div
      className={cn(
        "absolute inset-x-0 bottom-0 z-1000 flex items-center justify-between rounded-br-xl border-t border-grey-200 bg-background px-8 py-4",
        className,
      )}
    >
      <div className="flex flex-row items-center text-text-secondary">
        <Button
          variant="ghost"
          size="iconSm"
          onClick={onClear}
          className="-ml-3"
        >
          <IconX strokeWidth={2} size={16} />
        </Button>
        <p className="text-sm font-normal">
          {selectedLabel ?? `${selectedCount} selected`}
        </p>
      </div>
      <div className="flex items-center gap-3">{children}</div>
    </div>
  );
}

type AssetSelectorMediaCardProps = {
  src: string;
  thumbnailSrc: string | null;
  width?: number | null;
  height?: number | null;
  tags?: string[];
  isSelected: boolean;
  onSelect: () => void;
  coverFit?: boolean;
  showSelectIcon?: boolean;
  mediaType?: "image" | "video";
};

const CARD_ASPECT_RATIO = 200 / 133;
const THRESHOLD = 0.3;

function AssetSelectorMediaCard({
  src,
  thumbnailSrc,
  width,
  height,
  tags: rawTags,
  isSelected,
  onSelect,
  coverFit = false,
  showSelectIcon = true,
  mediaType = "image",
}: AssetSelectorMediaCardProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isHoveringVideo, setIsHoveringVideo] = useState(false);

  const tags = useMemo(
    () => [...(rawTags || [])].sort((a, b) => a.localeCompare(b)),
    [rawTags],
  );
  const visibleTag = tags[0];
  const remainingCount = tags.length - 1;

  const objectFit = useMemo(() => {
    if (coverFit) return "object-cover";
    if (!width || !height) return "object-contain";

    const imageAspectRatio = width / height;
    const ratio = imageAspectRatio / CARD_ASPECT_RATIO;

    if (ratio >= 1 - THRESHOLD && ratio <= 1 + THRESHOLD) {
      return "object-cover";
    }
    return "object-contain";
  }, [width, height, coverFit]);

  const handleMouseEnter = () => {
    if (mediaType !== "video") return;
    setIsHoveringVideo(true);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (mediaType !== "video") return;
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setIsHoveringVideo(false);
  };

  return (
    <div
      onClick={onSelect}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
      className={cn(
        "cursor-pointer p-0 focus-visible:outline-2",
        isSelected
          ? "rounded-[10px] border-2 border-transparent"
          : "m-px rounded-[9px] border",
      )}
      style={
        isSelected
          ? {
              background:
                "linear-gradient(white, white) padding-box, linear-gradient(to bottom right, var(--color-primary-400), var(--color-secondary-400)) border-box",
            }
          : undefined
      }
    >
      <div className="relative aspect-200/133 rounded-md">
        {showSelectIcon && (
          <Checkbox
            checked={isSelected}
            className="absolute top-3 left-3 z-10"
          />
        )}
        <div
          className="h-full w-full rounded-md"
          style={{
            backgroundImage:
              "linear-gradient(45deg, #F7F7F7 25%, transparent 25%), linear-gradient(-45deg, #F7F7F7 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #F7F7F7 75%), linear-gradient(-45deg, transparent 75%, #F7F7F7 75%)",
            backgroundSize: "16px 16px",
            backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px",
          }}
        >
          {mediaType === "video" && isHoveringVideo ? (
            <video
              ref={videoRef}
              src={src}
              poster={thumbnailSrc ?? undefined}
              muted
              loop
              playsInline
              autoPlay
              preload="auto"
              onLoadedData={() => {
                videoRef.current?.play().catch(() => {});
              }}
              className={cn("h-full w-full rounded-md object-cover", objectFit)}
            />
          ) : (
            <Image
              src={thumbnailSrc ?? src}
              className={cn("h-full w-full rounded-md object-cover", objectFit)}
            />
          )}
        </div>
        {tags.length > 0 && (
          <div className="absolute bottom-3 left-3 flex items-center gap-2">
            <Badge variant="tertiary">{visibleTag}</Badge>
            {remainingCount > 0 && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <div>
                    <Badge variant="tertiary" className="cursor-default">
                      +{remainingCount}
                    </Badge>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{tags.slice(1).join(", ")}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AssetSelectorDropzoneOverlay({
  isUploadingTitle,
  description,
  isUploading,
  isElementsEmpty,
}: {
  isUploadingTitle: string;
  description: string;
  isUploading: boolean;
  isElementsEmpty: boolean;
}) {
  return (
    <div
      className={cn(
        "absolute inset-0 z-50 flex items-center justify-center rounded-br-xl",
        isElementsEmpty && "rounded-br-xl border-t border-grey-200",
        isUploading ? "bg-white/95" : "bg-primary-100/95",
      )}
    >
      {isUploading ? (
        <div className="flex items-center gap-2">
          <LoadingSpinner size={20} />
          <p className="text-sm font-medium text-text-primary">
            {isUploadingTitle}
          </p>
        </div>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center rounded-br-xl border border-dashed border-primary-400">
          <IconUpload stroke={1.5} size={32} color="url(#icon-gradient)" />
          <h1 className="mt-4 text-base font-semibold">Drop file here</h1>
          <p className="text-sm text-text-secondary">{description}</p>
        </div>
      )}
    </div>
  );
}

function AssetSelectorEmptyState({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="flex h-full flex-1 flex-col items-center justify-center px-8 py-12">
      <IconFilter className="mb-3 text-text-primary" size={32} stroke={1.5} />
      <p className="mb-0.5 text-sm font-medium text-text-secondary">{title}</p>
      <p className="text-sm text-text-secondary">{description}</p>
    </div>
  );
}

function MediaGrid<T>({
  items,
  isLoading,
  emptyState,
  hideEmptyState,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  getItemKey,
  renderItem,
}: {
  items: T[];
  isLoading: boolean;
  emptyState: ReactNode;
  hideEmptyState: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  getItemKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
}) {
  const {
    scrollContainerRef,
    columnsPerRow,
    totalRows,
    rowVirtualizer,
    virtualItems,
  } = useImagesGridVirtualizer({
    itemCount: items.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const content = (() => {
    if (isLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <LoadingSpinner size={24} />
        </div>
      );
    }

    if (items.length === 0) {
      if (hideEmptyState) return null;
      return emptyState;
    }

    return (
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {virtualItems.map((virtualRow) => {
          const isLoaderRow = virtualRow.index >= totalRows;
          const startIndex = virtualRow.index * columnsPerRow;
          const rowItems = items.slice(startIndex, startIndex + columnsPerRow);

          return (
            <div
              key={virtualRow.index}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {isLoaderRow ? (
                <div className="flex justify-center py-4">
                  {isFetchingNextPage && <LoadingSpinner size={20} />}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {rowItems.map((item) => (
                    <div key={getItemKey(item)}>{renderItem(item)}</div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  })();

  return (
    <div
      ref={scrollContainerRef}
      className="h-full overflow-y-auto px-8 pt-6 pb-24"
    >
      {content}
    </div>
  );
}

export {
  AssetSelectorDialog,
  AssetSelectorMediaCard,
  AssetSelectorLayout,
  AssetSelectorHeader,
  AssetSelectorBody,
  AssetSelectorSidebar,
  AssetSelectorContent,
  AssetSelectorBottomBar,
  AssetSelectorEmptyState,
  AssetSelectorDropzoneOverlay,
  MediaGrid,
};
